import React from 'react';
import { t } from 'i18next';

import _ from 'lodash';

import { getImageURL } from './urls';
import { calculateWorstCasesByImages, separateLateralImages } from './calculations';
import { enUiVisibleTexts } from '../translation/en';
import { deUiVisibleTexts } from '../translation/de';
import { getDeployRegion, getRegionSpecificVATText } from './configurationHelpers';

import {
  AMD_LITE,
  DR_FULL,
  DR_LITE,
  GC_LITE,
  GERMANY,
  GLAUCOMA_CDR,
  RETINO_SCAN,
  RETINO_SCAN_ADV,
  RETINO_SCAN_ALL_SERVICES,
  RETINO_SCAN_SERVICES,
  SERVICE_AUDIT_FIELD_NAMES,
} from '../constants/constants';
import { LR_RESULT_ENUMS, GENERAL_RESULT_ENUMS } from '../constants/results';
import { EMAIL, EMAIL_REGEX, PHONE_NUMBER } from '../constants/validations';
import {
  BASIC_PERMISSIONS,
  STANDARD_PERMISSIONS,
  PREMIUM_PERMISSIONS,
  REGION_CURRENCIES,
} from '../constants/subscriptions';
import theme from '../styles/theme';

export const stringCapitalize = (str) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
export const getValuesGenerateKeys = (arg) => {
  const replacingSymbols = [
    ' ',
    '\n',
    '’',
    ',',
    "'",
    '-',
    ':',
    '.',
    '=',
    '?',
    '&',
    '*',
    '%',
    '$',
    '#',
    '@',
    '!',
    '^',
    '(',
    ')',
    '+',
    '[',
    ']',
    '{',
    '}',
    '|',
    '/',
  ];
  let result = { name: arg };
  replacingSymbols.map((symbol) => {
    result.name = { ...result }.name?.replaceAll(symbol, '_');
  });
  return result.name;
};
export const getTranslationValueFromKey = (text) => {
  const fetchingUiVisibleTexts = enUiVisibleTexts.en;
  let keyName;
  Object.keys(fetchingUiVisibleTexts).forEach((key) => {
    if (fetchingUiVisibleTexts[key] === text) {
      keyName = key;
    } else if (!keyName) {
      keyName = text;
    }
  });
  return keyName;
};

export const applyTranslationToValues = () => {
  const translationWords = { ...enUiVisibleTexts.en };
  Object.keys(translationWords).map(
    (textkey) => (translationWords[textkey] = t(getTranslationValueFromKey(translationWords[textkey])))
  );
  return translationWords;
};

export const setLocalStorageWithExpire = (key, value, secondsToLive) => {
  const now = new Date();
  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value: value,
    timeToLive: now.getTime() + secondsToLive * 1000,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

export const getLocalStorageWithExpire = (key) => {
  const itemString = localStorage.getItem(key);
  if (!itemString) {
    return null;
  }
  const item = JSON.parse(itemString);
  const now = new Date();
  if (now.getTime() > item.timeToLive) {
    // If the item is expired, delete the item from storage
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
};

/**
 * Conditionally wrap an element
 * @param {boolean} condition the condition to determine whether to wrap the element
 * @param {boolean} visible the condition to determine whether the element is visible
 * @param {ReactElement || DetailedReactHTMLElement || JSX.Element}  wrapper ConditionalWrapper component
 * @param {ReactElement || String} children the element to be wrapped
 * @returns {ReactElement || DetailedReactHTMLElement} wrapped children if condition is true otherwise return the children itself
 */
export const ConditionalWrapper = ({ condition, visible = true, wrapper, children }) => {
  if (!visible) {
    return null;
  }
  if (condition) {
    return React.cloneElement(wrapper, null, children);
  }
  return children;
};

export const allotObjectToTier = (planName, basicObj, standardObj, premiumObj) => {
  if (planName?.includes('Basic')) {
    return basicObj;
  } else if (planName?.includes('Standard')) {
    return standardObj;
  } else if (planName?.includes('Premium')) {
    return premiumObj;
  } else {
    // TODO error
  }
};

export const dictCheck = (dict, key) => {
  if (key in dict) {
    return dict[key];
  } else {
    return null;
  }
};

export const formatDateToString = (date) => {
  if (!date || date === '-') {
    return '-';
  } else {
    const [year, month, day] = date.split('-');
    return `${day}-${month}-${year}`;
  }
};

/**
 * Encode file to base64 string
 * @param {obj} file the file to be converted to base64
 * @returns {string} Converted Base64 string or empty string if no file is provided
 */
export const convertFileToBase64 = async (file) => {
  if (!file) return;
  return new Promise((resolve, reject) => {
    let fileReader = new FileReader();
    fileReader.onload = (e) => {
      const { result } = e.target;
      resolve(result);
    };
    fileReader.onerror = reject;
    fileReader.readAsDataURL(file);
  });
};

/**
 * Checks if string is in valid base64 format
 * @param {string} str String to be checked
 * @returns {boolean} Boolean toggle of whether string is in valid base64 format
 */
export const isBase64 = (str = '') => {
  const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
  return base64regex.test(str);
};

export const determineToolTipTitleContent = (requiredPermissions) => {
  let body = t(getTranslationValueFromKey('Upgrade your subscription if you wish to select this service'));
  if (requiredPermissions.every((permission) => BASIC_PERMISSIONS.indexOf(permission) > -1)) {
    return [t(getTranslationValueFromKey('Basic Plan required!')), body];
  } else if (requiredPermissions.every((permission) => STANDARD_PERMISSIONS.indexOf(permission) > -1)) {
    return [t(getTranslationValueFromKey('Standard Plan required!')), body];
  }
  // else if (requiredPermissions.every(permission => (BASIC_PLUS_PERMISSIONS && STANDARD_PLUS_PERMISSIONS).indexOf(permission) > -1)) {
  //     return ["Plus or Premium Plan required!", body]
  // }
  else if (requiredPermissions.every((permission) => PREMIUM_PERMISSIONS.indexOf(permission) > -1)) {
    return [t(getTranslationValueFromKey('Premium Plan required!')), body];
  } else {
    return null;
  }
};

/**
 * Sort service results based on retinoScan or retinoScan+
 * @param {array} serviceResults the results to categorize
 * @returns {array} an array contains sorted retinoScan or retinoScan+ service results
 */
export const categorizeServiceResults = (serviceResults = []) => {
  return serviceResults.reduce((acc, curr) => {
    if (RETINO_SCAN_ALL_SERVICES.includes(curr.service_type)) {
      const serviceIndex = RETINO_SCAN_ALL_SERVICES.indexOf(curr.service_type);
      // sort retinoScan services in GLAUCOMA_CDR and DR FULL
      acc[serviceIndex] = curr;
    }
    return acc;
  }, []);
};

/**
 * Combine service results into a single array
 * @param {array} serviceResults the results to combine
 * @returns {array} an array containing all service results
 */
export const combineServiceResults = (serviceResults = []) => {
  // Manually add service type 4 to array of service results to be same as service type 9
  const cdrService = serviceResults.find((serviceResult) => serviceResult?.service_type === 9);
  const serviceResultsWithCDR = [...serviceResults, { ...cdrService, service_type: 4 }];

  // Define the desired order of service types
  const order = serviceResults.some((result) => result?.service_type === 5) ? [5, 9, 7, 4] : [3, 9, 7, 4];

  // Sort the serviceResults array based on the order array
  const sortedServiceResults = serviceResultsWithCDR
    .filter((el) => order.includes(el.service_type))
    .sort((a, b) => {
      return order.indexOf(a.service_type) - order.indexOf(b.service_type);
    });

  // Combine the sorted results into the first array of the two
  return sortedServiceResults.reduce((combinedResults, result) => {
    combinedResults.push(result);
    return combinedResults;
  }, []);
};

/**
 * process images to get formatted service results
 * @param {array} examEyeImages the images with service results
 * @returns {array} an array contains formatted service results
 */
export const getServiceResultsByImages = (examEyeImages) => {
  if (_.isEmpty(examEyeImages)) return [];
  const leftRightIndexMap = {
    0: LR_RESULT_ENUMS.LEFT,
    1: LR_RESULT_ENUMS.RIGHT,
  };

  const separatedLeftRightImages = separateLateralImages(examEyeImages);
  const separatedWorstImageGradingServices = Object.values(separatedLeftRightImages).map((leftOrRightImages) =>
    calculateWorstCasesByImages(leftOrRightImages)
  );
  let serviceResults = separatedWorstImageGradingServices.reduce(
    (serviceResults, worstImageGradingServices, currentIndex) => {
      worstImageGradingServices.map((imageGradingService) => {
        let { id, service_type, ...results } = imageGradingService;
        let serviceResult = serviceResults.find((serviceResult) => serviceResult.service_type === service_type);
        if (serviceResult) {
          serviceResult.laterality.push({
            laterality: leftRightIndexMap[currentIndex],
            ...results,
          });
        } else {
          serviceResult = {
            service_type: imageGradingService.service_type,
            laterality: [
              {
                laterality: leftRightIndexMap[currentIndex],
                ...results,
              },
            ],
          };
          serviceResults.push(serviceResult);
        }
      });
      return serviceResults;
    },
    []
  );
  return serviceResults.filter((serviceResult) =>
    [DR_LITE, DR_FULL, AMD_LITE, GC_LITE, GLAUCOMA_CDR].includes(serviceResult.service_type)
  );
};

/**
 * Creates formatted eye result object to send back as payload to review endpoints
 * @param {object} rawEyeResult Service object
 * @return {object} Formatted eye result
 */
export const formatEyeResult = (rawEyeResult) => {
  const reviewFields = Object.values(SERVICE_AUDIT_FIELD_NAMES);
  let formattedEyeResult = {};

  reviewFields.forEach((fieldName) => {
    if (rawEyeResult?.[fieldName] && rawEyeResult?.[fieldName] !== GENERAL_RESULT_ENUMS.NOT_APPLICABLE) {
      formattedEyeResult[fieldName] = rawEyeResult[fieldName];
    }
  });

  return formattedEyeResult;
};

/**
 * Creates processed results object for ease of use.
 * @param {object} service Service object
 * @returns {object} Object containing serviceType, leftEyeResult and rightEyeResult properties
 */
export const processServiceResults = (service = {}) => {
  return {
    serviceType: service.service_type,
    leftEyeResult: service.laterality?.find((laterality) => laterality.laterality === LR_RESULT_ENUMS.LEFT),
    rightEyeResult: service.laterality?.find((laterality) => laterality.laterality === LR_RESULT_ENUMS.RIGHT),
  };
};

export const getCDRImagesFromServiceResults = (serviceResults = []) => {
  const cdrService = serviceResults.find((service) => service?.service_type === 9);
  return cdrService?.laterality?.reduce((acc, curr) => {
    acc.push({
      laterality: curr?.laterality,
      cdr_image: curr?.cdr_image ? getImageURL(curr?.cdr_image) : '',
      cdr_image_visible:
        curr?.cdr_image_visible === null || curr?.cdr_image_visible === undefined ? true : curr?.cdr_image_visible,
    });
    return acc;
  }, []);
};

export const toTrunc = (number, precision = 2) => {
  let regex = RegExp(`^\\d+(?:\\.\\d{0,${precision}})?`);
  if (typeof number === 'number') {
    return number.toString().match(regex)?.[0];
  }
};

/* Check if string is valid UUID */
export const checkIfValidUUID = (str) => {
  // Regular expression to check if string is a valid UUID
  const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
  return regexExp.test(str);
};

/**
 * Strip string from pattern and limit to maxLength
 * @param str
 * @param pattern
 * @param maxLength
 * @returns {string}
 */
export const stripString = (str = '', pattern, maxLength) => {
  return str.replace(pattern, '').slice(0, maxLength);
};

export const isEmailOrPhoneNumber = (str, excludeDialCode = false) => {
  // Regular expression to match phone numbers
  let phoneRegex;
  if (excludeDialCode) {
    phoneRegex = /^[0-9]{1,4}[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,4}$/;
  } else {
    phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,4}$/;
  }
  // Check if the input string matches either of the regex patterns
  if (EMAIL_REGEX.test(str)) {
    return EMAIL;
  } else if (phoneRegex.test(str)) {
    return PHONE_NUMBER;
  }
};

export const isValidEmail = (email) => {
  return EMAIL_REGEX.test(email);
};

/**
 * Parses error responses returned from the backend API.
 *
 * @param {object} body - The error response body from the backend.
 * @param {string} defaultMsg - The default error message to use if no specific message is found.
 * @returns {string} Parsed error message.
 */
export function parseError(body, defaultMsg, t) {
  // Use the provided default message if available, or a generic one.
  let message = defaultMsg || 'An error occurred, please try again later.';

  let field = '';

  // If the error response or error array is missing, return the default message.
  if (!body || !body.errors || !body.errors.length) {
    return defaultMsg;
  }

  // Extract the error message and field from the first error in the array.
  message = body?.errors?.[0]?.message;
  field = body?.errors?.[0]?.field;

  // Remove square brackets from the message, if present.
  if (message.startsWith('[') && message.endsWith(']')) {
    message = message.slice(1, -1);
  }

  // Convert underscores in field name to spaces.
  const fieldName = field ? t(field) : '';

  // If both the message and field name are available, format the error message with the field name.
  if (message && fieldName) {
    return `${fieldName.toUpperCase()}: ${message}`;
  }

  return message || defaultMsg;
}

/**
 * Check if a specific cookie exists in the current document.
 *
 * @param {string} cookieName - The name of the cookie to check for.
 * @returns {boolean} True if the cookie exists, false otherwise.
 */
export function checkCookie(cookieName) {
  // Split all cookies into an array and iterate through them.
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();

    // If the current cookie starts with the specified name, the cookie exists.
    if (cookie.startsWith(cookieName + '=')) {
      return true; // Cookie found
    }
  }
  // If no matching cookie is found, return false.
  return false; // Cookie not found
}
// Payment gateway
// Concatinating currency symbol with strings
export const concatinateCurrencySymbol = (values, planOptions) => {
  const copy_planOptions = { curr: '', planOptions: [...planOptions] };

  values.forEach((element) => {
    if (element?.stripe_price && element.stripe_price?.currency && element.stripe_price.currency) {
      copy_planOptions.curr = element.stripe_price.currency;
      copy_planOptions.planOptions[0].description = '';
      copy_planOptions.planOptions[0].description_1 = 'Price listed is in ';
      copy_planOptions.planOptions[0].description_2 = 'Choose your preferred prepaid amount:';
      copy_planOptions.planOptions[0].regional_currency = REGION_CURRENCIES[element.stripe_price.currency]?.text;
      copy_planOptions.planOptions[0].regional_currency_symbol =
        REGION_CURRENCIES[element.stripe_price.currency]?.symbol;
      copy_planOptions.planOptions[0].description_3 = ' and does not include GST. Applicable taxes will be added.';
      copy_planOptions.planOptions[1].description =
        '* A minimum subscription period of 3 months is required for first time purchase.';
      copy_planOptions.planOptions[1].description_1 = 'Price listed is in ';
      copy_planOptions.planOptions[1].description_2 = 'Choose your preferred monthly plan*:';
      copy_planOptions.planOptions[1].regional_currency = REGION_CURRENCIES[element.stripe_price.currency]?.text;
      copy_planOptions.planOptions[1].regional_currency_symbol =
        REGION_CURRENCIES[element.stripe_price.currency]?.symbol;
      copy_planOptions.planOptions[1].description_3 = ' and does not include GST. Applicable taxes will be added.';
    }
  });
  return copy_planOptions;
};

// Calculating the GST+Basic Amount
export const calculateGST = (baseAmount, gst, index) => {
  const gstAmount = parseFloat(baseAmount) * (parseFloat(gst) / 100);
  const basicAmount = baseAmount - gstAmount;
  const totalAmount = baseAmount + gstAmount;
  const arr = [gstAmount, totalAmount, baseAmount];
  return arr[index];
};
// Calculating the Discount Price
export const calculateDiscountedPrice = (originalPrice, discountRate) => {
  const discountAmount = originalPrice * (discountRate / 100);
  const finalPrice = originalPrice - discountAmount;
  return finalPrice;
};

// Splitting Billing Details
export const splitBillingDetails = (data) => {
  if (!!data?.test_mode) {
    return data;
  } else if (!data.test_mode) {
    let destructureData = {
      ...data,
      subscription: [],
      prepaid: [],
      prepaid_expiry_date: '',
      subscription_errorText: { error: '', extraMessage: '' },
    };

    if (
      data?.prepaid_expiry_date &&
      !!data.prepaid_expiry_date &&
      !isPlanExpired(data.prepaid_expiry_date) &&
      data?.prepaid_usages &&
      data.prepaid_usages.length > 0
    ) {
      const copyPrepaid = data.prepaid_usages
        .filter((item) => item.invoice !== null)
        .sort((a, b) => new Date(a.invoice.effective_at) - new Date(b.invoice.effective_at))
        .filter((obj) => obj.exam_balance > 0 && !!obj.is_active)
        .sort((a, b) => a.exam_balance - b.exam_balance);
      destructureData.prepaid = copyPrepaid;
      destructureData.prepaid_expiry_date = data.prepaid_expiry_date;
    }
    if (data?.subscription_usages && data.subscription_usages.length > 0) {
      const failureMessage1 = 'Your last transaction failed. Please review and complete the payment';
      const failureMessage2 = '. For help, contact';

      const copySubscription = data.subscription_usages.sort(
        (a, b) => new Date(b.subscription.start_date) - new Date(a.subscription.start_date)
      );

      destructureData.subscription = [copySubscription[0]];
      const { status } = copySubscription[0]?.subscription || {};

      if (status) {
        if (['incomplete', 'unpaid', 'past_due', 'paused'].includes(copySubscription[0].subscription.status)) {
          destructureData.subscription_errorText['error'] = failureMessage1;
          destructureData.subscription_errorText['extraMessage'] = failureMessage2;
        } else if (copySubscription[0].subscription.status === 'canceled') {
          (destructureData.subscription_errorText['error'] =
            'Your subscription has been canceled, please contact us at'),
            (destructureData.subscription_errorText['extraMessage'] = '');
        } else if (copySubscription[0].subscription.status === 'incomplete_expired') {
          (destructureData.subscription_errorText['error'] = ''),
            (destructureData.subscription_errorText['extraMessage'] = '');
        } else if (copySubscription[0].subscription.status === 'active') {
          destructureData.subscription_errorText['error'] =
            'Your current monthly subscription plan is active. If you would like to make any changes to your subscription, please contact us at';
          destructureData.subscription_errorText['extraMessage'] = '';
        } else if (copySubscription[0].subscription.status === 'trialing') {
          destructureData.subscription_errorText['error'] =
            'Your monthly subscription free trial is active. If you would like to make any changes to your subscription, please contact us at';
          destructureData.subscription_errorText['extraMessage'] = '';
        }
      }
    }

    return destructureData;
  } else {
    return { test_mode: true };
  }
};
// All prices are empty array will return true
export const isAllPricesEmpty = (price) => {
  return Object.values(price).every((arr) => arr.every((item) => Array.isArray(item) && item.length === 0));
};
const calculateOverdueStatus = (due_date) => {
  const now = new Date();
  const dueDate = new Date(due_date);

  let status = 'open';

  if (now.getTime() > dueDate.getTime()) {
    status = 'overdue';
  }
  return status;
};
// spliting billing history
export const splitingBillingHistory = (billing) => {
  const labelMap = {
    base_sub: 'Pay_per_Scan',
    image_storage: 'Image_Storage',
  };
  const data = { ...billing };
  if (data.test_mode) {
    return true;
  } else if (!data.test_mode) {
    let billHistory = [];

    if (data?.billing_history) {
      if (data.billing_history.length > 0) {
        data.billing_history.map((history, index) => {
          if (history?.prepaid && history.prepaid.length > 0) {
            if (history.status !== 'draft' && history.status !== 'incomplete' && history.status !== 'deleted') {
              const prepaidObj = {
                invoice_no: history.invoice_no,
                invoice_pdf: history.invoice_pdf,
                invoice_hosted_invoice: history.hosted_invoice_url,
                status: history.status,
                currency: history.currency,
                effective_at: history.effective_at,
                total: (history.total / 100).toFixed(2),
                credits:
                  history.prepaid[0].invoice.exam_credits < 10
                    ? `0${history.prepaid[0].invoice.exam_credits}`
                    : history.prepaid[0].invoice.exam_credits,
                label: 'Prepaid',
                key: 'prepaid',
              };
              billHistory.push(prepaidObj);
            }
          } else if (history?.subscription && history.subscription.length > 0) {
            if (history.status !== 'draft' && history.status !== 'incomplete' && history.status !== 'deleted') {
              const credits = history?.subscription?.[0]?.price?.exam_credits ?? 0;
              const subPlan = history?.subscription?.[0]?.price?.service_type ?? '';

              const subscriptionObj = {
                invoice_no: history.invoice_no,
                invoice_pdf: history.invoice_pdf,
                invoice_hosted_invoice: history.hosted_invoice_url,
                status: history.status,
                currency: history.currency,
                total: (history.total / 100).toFixed(2),

                credits: credits > 0 ? String(credits).padStart(2, '0') : String(credits),
                effective_at: history.effective_at,
                label: getDeployRegion() !== GERMANY ? 'Monthly' : labelMap[subPlan],
                key: 'subscription',
              };
              if (history.status === 'open' && !!history?.due_date) {
                subscriptionObj.status = calculateOverdueStatus(history.due_date);
              }
              billHistory.push(subscriptionObj);
            }
          }
        });
        billHistory = billHistory?.sort((a, b) => new Date(b.effective_at) - new Date(a.effective_at)) || [];
        return billHistory;
      } else {
        return [];
      }
    } else {
      return [];
    }
  }
};
// Is Plan expired
export function isPlanExpired(expiryDate) {
  const now = new Date(); // Get the current date and time
  const expirationDate = new Date(expiryDate); // Convert the expiry date string to a Date object
  // Compare the current date with the expiration date
  if (now > expirationDate) {
    return true; // The plan is expired
  } else {
    return false; // The plan is still active
  }
}

// Password validation
export const validatePassword = (event, setErrorMessage) => {
  const { value } = event.target;
  if (!value) return;

  if (value.length < 8) {
    setErrorMessage('Your password needs to contain at least 8 characters.');
  } else if (/^\d+$/.test(value)) {
    setErrorMessage("Your password can't be entirely numeric.");
  } else setErrorMessage('');
};

export const isCDRImageVisible = (imageVisible) => {
  // Default to true if imageVisible is undefined or null
  if (imageVisible === undefined || imageVisible === null) return true;
  return imageVisible;
};

export const formatNumber = (input) => {
  if (!input) return '';
  // Ensure the input is a string, remove non-numeric characters
  const numericValue = parseFloat(input.toString().replace(/[^\d.-]/g, ''));

  // Format the number with a comma as the thousands separator
  return new Intl.NumberFormat('en-US').format(numericValue);
};

export const dateFormated = (dateString) => {
  if (dateString !== null) {
    // Convert to Date object
    const date = new Date(dateString);
    // Extract the date components in local time
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');

    // Format as YYYY-MM-DD
    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  }
  return dateString;
};
// Check if the date of birth is valid
function isValidDOB(dob, allowDate) {
  const minDate = new Date(allowDate); // Minimum allowed date
  minDate.setHours(0, 0, 0, 0);
  const today = new Date(); // Current date
  today.setHours(0, 0, 0, 0);
  const dobDate = new Date(dob); // Convert input to Date object
  dobDate.setHours(0, 0, 0, 0);
  return dobDate >= minDate && dobDate < today;
}
function isValidDate(year, month, day) {
  if (month < 1 || month > 12) return false; // Month must be between 1 and 12
  if (day < 1) return false; // Day cannot be less than 1

  const maxDays = new Date(year, month, 0).getDate(); // Get the last day of the month
  return day <= maxDays;
}

export function testDOB(dobString, minimumAllowedDate = '1900-01-01') {
  // Function to validate the date format (YYYY-MM-DD)
  function isValidDateFormat(dobString) {
    const datePattern = /^\d{4}-\d{2}-\d{2}$/; // Matches YYYY-MM-DD
    return datePattern.test(dobString);
  }
  if (!isValidDateFormat(dobString)) {
    return false;
  }
  const [year, month, day] = dobString.split('-').map(Number);
  if (isValidDate(year, month, day)) {
    return isValidDOB(dobString, minimumAllowedDate);
  } else {
    return false;
  }
}
export function formatWithDecimals(input) {
  if (!input) return false;
  // Remove commas and convert to number
  let num = parseFloat(input.toString().replace(/,/g, ''));
  // Return with 2 decimal places
  return num.toFixed(2);
}
// Formating subscription array
export function formatingSubsriptionArray(subscriptionData) {
  const subscription = { ...subscriptionData };
  const { add_ons } = subscription;
  delete subscription.add_ons;
  const usages = [subscription];
  if (
    usages.length > 0 &&
    usages[0]?.status &&
    usages[0].status === 'active' &&
    add_ons.length > 0 &&
    ['trialing', 'active'].includes(add_ons[0].status) &&
    add_ons[0]?.price?.service_type === 'image_storage'
  ) {
    const addon = { ...add_ons[0], title: 'Image_Storage', subtitle: 'per_month' };
    usages.push(addon);
  }
  return usages;
}
export const getShapeColor = (cat) => {
  switch (cat) {
    case 'disease':
      return theme.annotation.green;
    case 'lesion':
      return theme.annotation.pink;
    default:
      return theme.palette.text.white;
  }
};
export const getLabelBackgroundColor = (cat) => {
  switch (cat) {
    case 'disease':
      return theme.annotation.labelGreen;
    case 'lesion':
      return theme.annotation.labelPink;
    default:
      return theme.palette.secondary.main;
  }
};
export const getIconColor = (isActive, isDisabled) => {
  if (isDisabled)
    return 'brightness(0) saturate(100%) invert(20%) sepia(20%) saturate(500%) hue-rotate(180deg) brightness(80%) contrast(90%)'; // Approximates #344160
  if (isActive) return 'brightness(0) invert(1)';
  return 'brightness(0) saturate(100%) invert(80%) sepia(30%) saturate(1000%) hue-rotate(180deg) brightness(100%) contrast(90%)';
};
